import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Loader2,
  CheckCircle2,
  XCircle,
  Ban,
  BadgeCheck,
  ChevronRight,
} from "lucide-react";
import { api } from "../../utils/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function fmtLastLogin(iso) {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Never";
  }
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .get("/admin/users")
      .then((data) => setUsers(data.users || []))
      .catch((err) => setError(err.message || "Failed to load users"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const hay = [
        u.fullName,
        u.email,
        u.accountNumber,
        u.phone?.e164 || u.phone || "",
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [users, search]);

  const open = (uid) => navigate(`/admin/users/${uid}`);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Users
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {users.length} total · {filtered.length} shown
          </p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone, account #"
            className="input-base pl-9"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-10 text-center text-sm text-gray-500">
          No users match your search.
        </div>
      ) : (
        <>
          {/* Desktop / tablet — table view */}
          <div className="card hidden overflow-x-auto p-0 md:block">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Name</th>
                  <th className="px-4 py-3 text-left font-medium">Email</th>
                  <th className="px-4 py-3 text-left font-medium">Account #</th>
                  <th className="px-4 py-3 text-left font-medium">Created</th>
                  <th className="px-4 py-3 text-center font-medium">Platform</th>
                  <th className="px-4 py-3 text-left font-medium">Last login</th>
                  <th className="px-4 py-3 text-right font-medium">Tenants</th>
                  <th className="px-4 py-3 text-right font-medium">Props</th>
                  <th className="px-4 py-3 text-center font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
                {filtered.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => open(u.id)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                      {u.fullName || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {u.email}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300 font-mono text-xs">
                      {u.accountNumber || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {fmtDate(u.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        u.signupPlatform === "mobile"
                          ? "bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-400 ring-1 ring-purple-600/20"
                          : "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-400 ring-1 ring-blue-600/20"
                      }`}>
                        {u.signupPlatform === "mobile" ? "Mobile" : "Web"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {fmtLastLogin(u.lastLoginAt)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                      {u.activeTenantCount ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                      {u.propertyCount ?? 0}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.deactivated ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-600/20">
                          <Ban className="h-3 w-3" />
                          Deactivated
                        </span>
                      ) : u.billingWaived ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-green-600/20">
                          <BadgeCheck className="h-3 w-3" />
                          Waived
                        </span>
                      ) : u.emailVerified ? (
                        <CheckCircle2 className="inline h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="inline h-4 w-4 text-gray-300" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile — card list */}
          <ul className="space-y-2 md:hidden">
            {filtered.map((u) => (
              <li
                key={u.id}
                onClick={() => open(u.id)}
                className="card flex cursor-pointer items-start gap-3 p-4 active:bg-gray-50 dark:active:bg-gray-800"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 font-medium text-gray-900 dark:text-white">
                      {u.fullName || u.email}
                    </div>
                    {u.deactivated ? (
                      <span className="flex-none rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700 ring-1 ring-red-600/20">
                        Deactivated
                      </span>
                    ) : u.billingWaived ? (
                      <span className="flex-none rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700 ring-1 ring-green-600/20">
                        Waived
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                    {u.email}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                    <span>#{u.accountNumber || "—"}</span>
                    <span>·</span>
                    <span>{u.activeTenantCount ?? 0} tenants</span>
                    <span>·</span>
                    <span>{u.propertyCount ?? 0} props</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                    Last login: {fmtLastLogin(u.lastLoginAt)}
                  </div>
                </div>
                <ChevronRight className="mt-1 h-4 w-4 flex-none text-gray-400" />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
